import { useEffect } from "react";

const SITE_NAME = "NextSkillAI";
const SITE_URL = "https://nextskillai.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.WebP`; // Put this image inside /public
const DEFAULT_TWITTER_HANDLE = "@NextSkillAI";
const DEFAULT_LOCALE = "en_US";

function createOrUpdateMeta({ selector, attrName, attrValue, content }) {
  let element = document.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attrName, attrValue);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function createOrUpdateLink(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function createOrUpdateScript(id, type, content) {
  let element = document.getElementById(id);

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = type;
    document.head.appendChild(element);
  }

  element.textContent = content;
}

function normalizeUrl(url = "") {
  if (!url) return SITE_URL;

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  if (url.startsWith("/")) {
    return `${SITE_URL}${url}`;
  }

  return `${SITE_URL}/${url}`;
}

function getCleanPath(pathname = "/") {
  if (!pathname || pathname === "") return "/";
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function buildCanonicalUrl(canonical) {
  if (canonical) {
    return normalizeUrl(canonical);
  }

  const currentPath = getCleanPath(window.location.pathname);
  return `${SITE_URL}${currentPath}`;
}

function removeTrailingSlash(url) {
  if (!url) return url;
  return url.length > 1 && url.endsWith("/") ? url.slice(0, -1) : url;
}

function useSEO({
  title = SITE_NAME,
  description = "Learn Excel, SQL, Python, Power BI, and practical data skills with structured tutorials on NextSkillAI.",
  keywords = "NextSkillAI, Excel tutorial, SQL tutorial, Python tutorial, Power BI tutorial, data analytics course, beginner data skills",
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  ogLocale = DEFAULT_LOCALE,
  twitterHandle = DEFAULT_TWITTER_HANDLE,
  author = "NextSkillAI",
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  schema = null,
  noIndex = false,
} = {}) {
  useEffect(() => {
    const fullTitle =
      title && title !== SITE_NAME ? `${title} | ${SITE_NAME}` : SITE_NAME;

    const canonicalUrl = removeTrailingSlash(buildCanonicalUrl(canonical));
    const finalOgImage = normalizeUrl(ogImage);

    document.title = fullTitle;

    createOrUpdateMeta({
      selector: 'meta[name="description"]',
      attrName: "name",
      attrValue: "description",
      content: description,
    });

    createOrUpdateMeta({
      selector: 'meta[name="keywords"]',
      attrName: "name",
      attrValue: "keywords",
      content: keywords,
    });

    createOrUpdateMeta({
      selector: 'meta[name="author"]',
      attrName: "name",
      attrValue: "author",
      content: author,
    });

    createOrUpdateMeta({
      selector: 'meta[name="robots"]',
      attrName: "name",
      attrValue: "robots",
      content: noIndex ? "noindex, nofollow" : robots,
    });

    createOrUpdateMeta({
      selector: 'meta[name="googlebot"]',
      attrName: "name",
      attrValue: "googlebot",
      content: noIndex ? "noindex, nofollow" : robots,
    });

    createOrUpdateLink("canonical", canonicalUrl);

    createOrUpdateMeta({
      selector: 'meta[property="og:title"]',
      attrName: "property",
      attrValue: "og:title",
      content: fullTitle,
    });

    createOrUpdateMeta({
      selector: 'meta[property="og:description"]',
      attrName: "property",
      attrValue: "og:description",
      content: description,
    });

    createOrUpdateMeta({
      selector: 'meta[property="og:url"]',
      attrName: "property",
      attrValue: "og:url",
      content: canonicalUrl,
    });

    createOrUpdateMeta({
      selector: 'meta[property="og:site_name"]',
      attrName: "property",
      attrValue: "og:site_name",
      content: SITE_NAME,
    });

    createOrUpdateMeta({
      selector: 'meta[property="og:type"]',
      attrName: "property",
      attrValue: "og:type",
      content: ogType,
    });

    createOrUpdateMeta({
      selector: 'meta[property="og:image"]',
      attrName: "property",
      attrValue: "og:image",
      content: finalOgImage,
    });

    createOrUpdateMeta({
      selector: 'meta[property="og:locale"]',
      attrName: "property",
      attrValue: "og:locale",
      content: ogLocale,
    });

    createOrUpdateMeta({
      selector: 'meta[name="twitter:card"]',
      attrName: "name",
      attrValue: "twitter:card",
      content: "summary_large_image",
    });

    createOrUpdateMeta({
      selector: 'meta[name="twitter:site"]',
      attrName: "name",
      attrValue: "twitter:site",
      content: twitterHandle,
    });

    createOrUpdateMeta({
      selector: 'meta[name="twitter:creator"]',
      attrName: "name",
      attrValue: "twitter:creator",
      content: twitterHandle,
    });

    createOrUpdateMeta({
      selector: 'meta[name="twitter:title"]',
      attrName: "name",
      attrValue: "twitter:title",
      content: fullTitle,
    });

    createOrUpdateMeta({
      selector: 'meta[name="twitter:description"]',
      attrName: "name",
      attrValue: "twitter:description",
      content: description,
    });

    createOrUpdateMeta({
      selector: 'meta[name="twitter:image"]',
      attrName: "name",
      attrValue: "twitter:image",
      content: finalOgImage,
    });

    const defaultSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: fullTitle,
      url: canonicalUrl,
      description,
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo192.png`,
        },
      },
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
    };

    createOrUpdateScript(
      "nextskillai-jsonld-primary",
      "application/ld+json",
      JSON.stringify(schema || defaultSchema, null, 2)
    );

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };

    createOrUpdateScript(
      "nextskillai-jsonld-website",
      "application/ld+json",
      JSON.stringify(websiteSchema, null, 2)
    );
  }, [
    title,
    description,
    keywords,
    canonical,
    ogImage,
    ogType,
    ogLocale,
    twitterHandle,
    author,
    robots,
    schema,
    noIndex,
  ]);
}

export default useSEO;